import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Star, Zap, Target, Award } from 'lucide-react';
import type { LitLegionPlayerStats } from '@/lib/api';

interface LitLegionStatsCardProps {
  stats: LitLegionPlayerStats | null;
  loading?: boolean;
}

export function LitLegionStatsCard({ stats, loading }: LitLegionStatsCardProps) {
  if (loading) {
    return <Skeleton className="h-28 rounded-lg" />;
  }

  if (!stats) {
    return null;
  }

  const xpProgress =
    stats.level && stats.nextLevel
      ? ((stats.xpAmount - stats.level.xpRequired) /
          (stats.nextLevel.xpRequired - stats.level.xpRequired)) *
        100
      : 0;

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500 shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">XP</p>
              <p className="font-semibold">{stats.xpAmount.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500 shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">Level</p>
              <p className="font-semibold">
                {stats.level ? `${stats.level.levelNumber} - ${stats.level.name}` : 'None'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-green-500 shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="font-semibold">{stats.tasksCompleted}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-500 shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">In Progress</p>
              <p className="font-semibold">{stats.tasksInProgress}</p>
            </div>
          </div>
        </div>

        {stats.nextLevel && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Progress to Level {stats.nextLevel.levelNumber}</span>
              <span>
                {stats.xpAmount.toLocaleString()} / {stats.nextLevel.xpRequired.toLocaleString()} XP
              </span>
            </div>
            <div className="h-2 rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.min(100, Math.max(0, xpProgress))}%` }}
              />
            </div>
          </div>
        )}

        {stats.earnedBadges.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Award className="h-4 w-4 text-purple-500" />
              <span>
                Earned badges
                <span className="ml-1 font-medium text-foreground">
                  ({stats.earnedBadges.length})
                </span>
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {stats.earnedBadges.map((badge) => (
                <div
                  key={badge.badgeId}
                  className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5"
                  title={badge.description}
                >
                  {badge.image ? (
                    <img
                      src={badge.image}
                      alt=""
                      className="h-6 w-6 rounded-sm object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Award className="h-5 w-5 text-purple-500 shrink-0" aria-hidden />
                  )}
                  <span className="text-sm font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
